import { PoolInstance, isInstance } from '@synergy/config';
import { ReactElement, useEffect } from 'react';
import { Route, Routes, useSearchParams } from 'react-router-dom';

import { ArticleDetail } from './pages/article-detail/article-detail';
import { CoverPage } from './pages/coverpage/coverpage';
import { ErrorPage } from './pages/errorpage/errorpage';
import { NewsPageDetail } from './pages/newspage/detail/newspage-detail';
import { NewsPageOverview } from './pages/newspage/overview/newspage-overview';
import { Orders } from './pages/orders/orders';
import { ROUTES } from './utils/routes';
import { RouteAccessControl } from './components/route-access-control/route-access-control';
import { SearchPage } from './pages/searchpage/search-page';
import { ShoppingCartEmpty } from './pages/shopping-cart/confirmation-pages/shopping-cart-empty';
import { ShoppingCartPage } from './pages/shopping-cart/shopping-cart-page';
import { ShoppingCartSubmit } from './pages/shopping-cart/confirmation-pages/shopping-cart-submit';
import { ShoppingCartSubmitInquiry } from './pages/shopping-cart/confirmation-pages/shopping-cart-submit-inquiry';
import { SlugRedirect } from './pages/slug-redirect/slug-redirect';
import { StaticSites } from './pages/static-sites/static-sites';
import { SupplierDetail } from './pages/supplier-directory/detail/supplier-detail';
import { SupplierDirectory } from './pages/supplier-directory/overview/supplier-directory';
import { WishListPage } from './pages/wish-list/wish-list-page';
import { WishListRedirect } from './pages/wish-list/wishl-list-redirect';
import { accountSelector } from './store/configuration-selectors';
import { closeInquiryModal, openInquiryModal } from './store/single-inquiry-actions';
import { useAppDispatch, useAppSelector } from '../store/store';

const ShowSingleInquiryUrlListener = (): ReactElement | null => {
    const dispatch = useAppDispatch();
    const [params] = useSearchParams();
    const showSingleInquiry = !!params.has('single-inquiry');

    useEffect(() => {
        if (showSingleInquiry) {
            dispatch(openInquiryModal());
        }
        return () => {
            dispatch(closeInquiryModal());
        };
    }, [showSingleInquiry, dispatch]);

    return null;
};

export const Routing = (): ReactElement | null => {
    const account = useAppSelector(accountSelector);

    if (!account) {
        return null;
    }

    return (
        <>
            {isInstance(PoolInstance.CH) && <ShowSingleInquiryUrlListener />}
            <Routes>
                <Route element={<RouteAccessControl redirectTo={ROUTES.searchPage} />}>
                    <Route path={ROUTES.coverPage} element={<CoverPage />} />
                    <Route path={ROUTES.cart.base} element={<ShoppingCartPage />} />
                    <Route path={ROUTES.cart.empty} element={<ShoppingCartEmpty />} />
                    <Route path={ROUTES.cart.submit} element={<ShoppingCartSubmit />} />
                    <Route path={ROUTES.cart.submitInquiry} element={<ShoppingCartSubmitInquiry />} />
                    <Route path={ROUTES.wishList.base} element={<WishListRedirect />} />
                    <Route path={ROUTES.wishList.detail} element={<WishListPage />} />
                    <Route path={ROUTES.orders + '/*'} element={<Orders />} />
                </Route>
                <Route
                    element={
                        <RouteAccessControl
                            redirectTo={ROUTES.supplierDirectory.detail.replace(':id', account.id.toString())}
                        />
                    }
                >
                    <Route path={ROUTES.supplierDirectory.base} element={<SupplierDirectory />} />
                </Route>
                <Route path={ROUTES.supplierDirectory.detail} element={<SupplierDetail />} />
                <Route path={ROUTES.news.base} element={<NewsPageOverview />} />
                <Route path={ROUTES.news.detail} element={<NewsPageDetail />} />
                <Route path={ROUTES.article.detail} element={<ArticleDetail />} />
                <Route path={ROUTES.notFound} element={<ErrorPage />} />
                <Route path={ROUTES.searchPage} element={<SearchPage />} />
                <Route path={ROUTES.view} element={<SlugRedirect />} />
                <Route path="*" element={<StaticSites />} />
            </Routes>
        </>
    );
};
